import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { JwtHelperService } from '@auth0/angular-jwt';
import { User, Response } from 'src/app/models';

export type UserCredentials = {
  email:    string;
  password: string;
};

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  // Token and user object
  authToken: any;
  user: any;
  isAdmin!: boolean;
  isSuperAdmin!: boolean;
  agencyInfo: any | null = {};

  constructor(private http: HttpClient, private helper: JwtHelperService) { }

  // Send request to backend with a user to register
  registerUser(data: any, captcha: string, orderID: string|null) {
    return this.http.post<Response<{ user: User; token: string }>>('api/v1/public/user/register', { ...data, captcha, orderID });
  }

  // Send request to backend to authenticate a user logging in
  authenticateUser(creds: UserCredentials, captcha: string) {
    return this.http.post('api/v1/public/user/authenticate', { ...creds, captcha });
  }

  // Send request to backend to authenticate an admin user logging in
  authenticateAdminUser(creds: UserCredentials, captcha: string) {
    return this.http.post('api/v1/public/adminUser/authenticate', { ...creds, captcha });
  }

  // Send request to backend to get profile information (account-info)
  getProfile() {
    return this.http.get('api/v1/users/profile');
  }

  // Send request to backend to get profile information (account-info)
  updateProfile(user: any) {
    // Load user token and add it to headers
    this.loadToken();

    return this.http.post('api/v1/users/update', user);
  }

  // Check if the email already exists
  checkEmail(email: any) {
    return this.http.post('api/v1/public/check-user-email', email);
  }

  // Send request to send email to reset password
  forgotpassword(email: any) {
    return this.http.post('api/v1/passwords/forgot', email);
  }

  // Send request to send email to reset password admins
  forgotAdminpassword(email: any) {
    return this.http.post('api/v1/passwords/admin-forgot', email);
  }

  // Send request to actually reset the password
  resetpassword(email: any) {
    return this.http.post('api/v1/passwords/reset', email);
  }

  // Send request to actually reset the password admins
  resetAdminpassword(email: any) {
    return this.http.post('api/v1/passwords/admin-reset', email);
  }

  // Send request to reset password but user is doing this from account overview instead
  resetpasswordloggedin(user: any) {
    return this.http.post('api/v1/passwords/resetloggedin', user);
  }

   // Send request to reset password but user is doing this from account overview instead admins
   resetAdminpasswordloggedin(user: any) {
    return this.http.post('api/v1/passwords/resetAdminloggedin', user);
  }

  // Use local storage to store jwt token for a session
  storeUserData(token: string, user: any) {
    localStorage.setItem('id_token', token);
    localStorage.setItem('user', JSON.stringify(user));
    this.authToken = token;
    this.user = user;
    this.isAdmin = false;
    this.isSuperAdmin = false;
  }

  // Use local storage to store jwt token and admin data for a session
  storeAdminData(token: string, user: any) {
    this.storeUserData(token, user);

    // See what kind of admin
    if (user.adminUser.isSuperAdmin) {
      this.isAdmin = true;
      this.isSuperAdmin = true;
    }

    // Agency Admins need their agency info too
    else {
      this.isAdmin = true;
      this.isSuperAdmin = false;
      this.agencyInfo = user.agency;
      localStorage.setItem('agencyData', JSON.stringify(user.agency));
    }
  }

  // Account info restore user changed
  updateUserDataStored() {
    localStorage.setItem('user', JSON.stringify(this.user));
  }

  // Store admin status if they successfully logged in
  storeAdminStatus(status: boolean) {
    localStorage.setItem('isAdmin', JSON.stringify(status));
    localStorage.setItem('isSuperAdmin', JSON.stringify(this.isSuperAdmin));
    this.isAdmin = status;
  }

  // Retrieve admin status
  getAdminStatus() {
    const isAdmin = localStorage.getItem('isAdmin') === 'true';
    return isAdmin;
  }

  getAdminRole() {
    const isSuperAdmin = localStorage.getItem('isSuperAdmin');

    if (isSuperAdmin === 'true') {
      this.isSuperAdmin = true;
      this.isAdmin = true;
    }
    else {
      this.isSuperAdmin = false;
      this.isAdmin = this.getAdminStatus();
    }
  }

  getAgencyData() {
    var my_object = JSON.parse(localStorage.getItem('agencyData') || '{}');

    this.agencyInfo = my_object;
  }

  // Using this to restore this.user and token when needed because it gets erased on page reload
  restoreData() {
    const userJSON = localStorage.getItem('user');
    if (userJSON) {
      this.user = JSON.parse(userJSON);
    }
    this.loadToken();
  }

  // Load token from storage
  loadToken() {
    const token = localStorage.getItem('id_token');
    this.authToken = token;
    return this.authToken;
  }

  // Check if the token is expired or not, not being used right now I think
  notLoggedIn() {
    return this.helper.isTokenExpired(this.authToken);
  }

  // Clear local storage and logout
  logout() {
    this.authToken = null;
    this.user = null;
    localStorage.clear();
  }
}
